// "5 minutes until night" / "5 minutes until day" rule.
//
// Tick-driven (not event-driven): the threshold is crossed by
// CLIENT-side interpolation between server.time snapshots, not by
// an event arrival. The 30s worker poll cadence means no event
// lands at exactly the 5-minute mark; the user's browser interpolates
// nextTransition.realSeconds smoothly each second, and this rule
// observes that.
//
// Fires once per upcoming transition. The dedupe key folds together
// the kind (until_day / until_night) AND the absolute sunrise/sunset
// game-hour values from the current snapshot, so the same transition
// can't fire twice but the NEXT transition (different kind) fires
// cleanly without the operator having to wait out the engine's
// default dedupe window.

import { nextTransition } from "@/lib/time/day-night";
import type { AnnouncementRule, Instruction } from "../types";

export const DAY_NIGHT_RULE_ID = "dayNightWarning";
export const DAY_NIGHT_DEFAULT_THRESHOLD_MIN = 5;

interface DayNightParams {
  thresholdMinutes?: number;
}

export const dayNightWarning: AnnouncementRule = {
  id: DAY_NIGHT_RULE_ID,
  label: '5-minute warning before day/night',
  defaultEnabled: true,
  onTick: (ctx): Instruction | null => {
    const snap = ctx.serverTime;
    if (!snap) return null;
    const threshold =
      Number((ctx.ruleParams as DayNightParams).thresholdMinutes ?? DAY_NIGHT_DEFAULT_THRESHOLD_MIN);
    if (!Number.isFinite(threshold) || threshold <= 0) return null;
    const thresholdSec = threshold * 60;
    const next = nextTransition(snap, ctx.nowMs);
    // Fire while we're inside the warning window. Engine dedupe makes
    // this idempotent across the ~thresholdSec tick band per transition.
    if (next.realSeconds > thresholdSec) return null;
    if (next.realSeconds < 0) return null;
    const label = next.kind === "until_day" ? "day" : "night";
    return {
      // Dedupe per (kind, sunrise, sunset) — the (sunrise, sunset) pair
      // changes only if the server's transition thresholds themselves
      // shift (server reconfig / wipe), so for a normal run the upcoming
      // sunrise and the next sunrise read as distinct keys (because the
      // OTHER kind fires in between and the dedupe TTL ages out).
      // Long window so we don't accidentally re-fire late in the same
      // approach window.
      dedupeKey: `dayNightWarning:${next.kind}:${snap.sunrise}:${snap.sunset}`,
      dedupeWindowMs: 30 * 60 * 1000,
      text: `${threshold} minutes until ${label}`,
    };
  },
};
